/**
 * SortButton Component
 *
 * Standalone sort control — trigger button that opens a Popover with sort field
 * and direction options. Designed to sit inline next to StatusFlow tabs.
 *
 * @example
 * ```tsx
 * <SortButton
 *   sortBy={sortBy}
 *   onSortChange={setSortBy}
 *   sortFieldLabels={{ date: 'Datum', amount: 'Částka', vendor: 'Dodavatel', number: 'Číslo' }}
 * />
 * ```
 */
import * as React from 'react';
import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import { Button } from './Button';
import { Icon } from './icons/Icon';
import { Label } from './Label';
import { cn, getTestId, type TestIdProps } from '../utils';
import { useTranslation } from '../providers/UIProvider';
import type { SidebarSortBy } from '../modules/filters/Filters';
import type { IconName } from '../icons/names';

export interface SortButtonProps extends TestIdProps {
  /** Current sort state */
  sortBy?: SidebarSortBy;
  /** Called when user picks a different field or direction */
  onSortChange?: (sort: SidebarSortBy) => void;
  /** Labels for each sort field — passed down from the consumer so they can be translated */
  sortFieldLabels?: Record<SidebarSortBy['field'], string>;
  /** Icons for each sort field */
  sortFieldIcons?: Record<SidebarSortBy['field'], IconName>;
  /** Additional CSS classes on the wrapper */
  className?: string;
  /** Additional CSS classes merged into the trigger button — use to override variant/color */
  buttonClassName?: string;
}

const DEFAULT_ICONS: Record<SidebarSortBy['field'], IconName> = {
  created: 'calendar-clock',
  date: 'calendar',
  amount: 'coins',
  vendor: 'building',
  number: 'file-invoice-dollar'
};

/**
 * SortButton — inline sort control that shows current field + direction and
 * opens a popover to change them. Shares i18n keys with `modules.sidebar`.
 */
export const SortButton = React.forwardRef<HTMLDivElement, SortButtonProps>(
  (
    {
      sortBy = { field: 'date', order: 'desc' },
      onSortChange,
      sortFieldLabels,
      sortFieldIcons,
      className,
      buttonClassName,
      testId,
      id
    },
    ref
  ) => {
    const t = useTranslation('modules.sidebar');

    const labels: Record<SidebarSortBy['field'], string> = sortFieldLabels ?? {
      date: t('sortFields.date'),
      amount: t('sortFields.amount'),
      vendor: t('sortFields.vendor'),
      number: t('sortFields.number')
    };

    // Fields without a known icon must never reach <Icon name={undefined}> — that throws.
    const iconFor = (field: SidebarSortBy['field']): IconName =>
      sortFieldIcons?.[field] ?? DEFAULT_ICONS[field] ?? 'arrow-down-wide-short';

    return (
      <div
        ref={ref}
        id={id}
        data-testid={getTestId({ testId, id })}
        className={cn('relative', className)}
      >
        <Popover>
          {({ close }) => (
            <>
              <PopoverButton
                as={Button}
                variant='default'
                outline='default'
                size='md'
                className={cn(
                  'min-w-[120px] rounded-md !flex !items-center !justify-between !gap-sm !flex-nowrap !bg-background-input hover:!bg-background-muted hover:!border-primary-base !px-md h-10',
                  buttonClassName
                )}
              >
                <span className='inline-flex items-center gap-sm shrink-0 whitespace-nowrap text-sm font-regular text-foreground-base'>
                  <Icon name='arrow-down-wide-short' className='!w-3.5 !h-3.5 shrink-0' />
                  <span className='whitespace-nowrap'>{labels[sortBy.field] ?? sortBy.field}</span>
                </span>
                <span className='shrink-0 text-base leading-none whitespace-nowrap pl-xs text-foreground-base'>
                  {sortBy.order === 'asc' ? '↑' : '↓'}
                </span>
              </PopoverButton>

              <Transition
                enter='transition ease-out duration-200'
                enterFrom='opacity-0 translate-y-1'
                enterTo='opacity-100 translate-y-0'
                leave='transition ease-in duration-150'
                leaveFrom='opacity-100 translate-y-0'
                leaveTo='opacity-0 translate-y-1'
              >
                <PopoverPanel className='w-72 p-sm bg-background-input border border-border-base rounded-lg shadow-lg z-50 mt-sm absolute right-0'>
                  <div className='space-y-md'>
                    {/* Sort field */}
                    <div className='space-y-xs'>
                      <Label size='xs' className='text-foreground-muted font-medium px-sm pb-sm'>
                        {t('sortBy')}
                      </Label>
                      <div className='space-y-xs pt-sm'>
                        {(Object.keys(labels) as SidebarSortBy['field'][]).map(field => (
                          <Button
                            key={field}
                            variant={sortBy.field === field ? 'primary' : 'ghost'}
                            className='w-full justify-start gap-sm rounded-md'
                            size='md'
                            onClick={() => {
                              onSortChange?.({ ...sortBy, field });
                              close();
                            }}
                          >
                            <Icon name={iconFor(field)} className='!w-4 !h-4 pr-sm' />
                            {labels[field]}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Sort direction */}
                    <div className='space-y-xs pt-sm border-t border-border-base'>
                      <Label size='xs' className='text-foreground-muted font-medium px-sm'>
                        {t('direction')}
                      </Label>
                      <div className='grid grid-cols-2 gap-sm pt-sm'>
                        <Button
                          variant={sortBy.order === 'desc' ? 'primary' : 'ghost'}
                          className='gap-sm rounded-md flex-nowrap'
                          size='md'
                          onClick={() => onSortChange?.({ ...sortBy, order: 'desc' })}
                        >
                          <span className='text-lg flex-shrink-0 whitespace-nowrap pr-sm'>↓</span>
                          <span className='flex-shrink-0 whitespace-nowrap'>{t('descending')}</span>
                        </Button>
                        <Button
                          variant={sortBy.order === 'asc' ? 'primary' : 'ghost'}
                          className='gap-sm rounded-md flex-nowrap'
                          size='md'
                          onClick={() => onSortChange?.({ ...sortBy, order: 'asc' })}
                        >
                          <span className='text-lg flex-shrink-0 whitespace-nowrap pr-sm'>↑</span>
                          <span className='flex-shrink-0 whitespace-nowrap'>{t('ascending')}</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                </PopoverPanel>
              </Transition>
            </>
          )}
        </Popover>
      </div>
    );
  }
);

SortButton.displayName = 'SortButton';
